import { useCallback, useEffect, useRef } from 'react';
import { isProductVariantSkuUnique } from 'services/product';

type Validator = (_: unknown, value: string) => Promise<void>;

interface UseUniquenessValidatorOptions {
  checkIsUnique: (value: string) => Promise<boolean>;
  existsMessage: string;
  failureMessage?: string;
  delayMs?: number;
}

const DEFAULT_FAILURE_MESSAGE = 'Validation failed. Try again.';

const useUniquenessValidator = ({
  checkIsUnique,
  existsMessage,
  failureMessage = DEFAULT_FAILURE_MESSAGE,
  delayMs = 500,
}: UseUniquenessValidatorOptions): Validator => {
  const timeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const cancelPendingValidationRef = useRef<(() => void) | null>(null);
  const latestValidationIdRef = useRef(0);

  const clearPendingValidation = useCallback(() => {
    if (timeoutRef.current) {
      clearTimeout(timeoutRef.current);
      timeoutRef.current = null;
    }

    if (cancelPendingValidationRef.current) {
      cancelPendingValidationRef.current();
      cancelPendingValidationRef.current = null;
    }
  }, []);

  useEffect(() => {
    return () => {
      latestValidationIdRef.current += 1;
      clearPendingValidation();
    };
  }, [clearPendingValidation]);

  return useCallback<Validator>(
    (_, value) => {
      if (!value) {
        latestValidationIdRef.current += 1;
        clearPendingValidation();
        return Promise.resolve();
      }

      clearPendingValidation();

      const validationId = ++latestValidationIdRef.current;

      return new Promise<void>((resolve, reject) => {
        cancelPendingValidationRef.current = resolve;

        timeoutRef.current = setTimeout(async () => {
          timeoutRef.current = null;
          cancelPendingValidationRef.current = null;

          try {
            const isUnique = await checkIsUnique(value);

            if (validationId !== latestValidationIdRef.current) {
              resolve();
              return;
            }

            if (!isUnique) {
              reject(new Error(existsMessage));
              return;
            }

            resolve();
          } catch {
            if (validationId !== latestValidationIdRef.current) {
              resolve();
              return;
            }

            reject(new Error(failureMessage));
          }
        }, delayMs);
      });
    },
    [checkIsUnique, existsMessage, failureMessage, clearPendingValidation, delayMs],
  );
};

export const useProductVariantSkuValidator = (delayMs = 500) =>
  useUniquenessValidator({
    checkIsUnique: isProductVariantSkuUnique,
    existsMessage: 'SKU already exists',
    delayMs,
  });
